import React from "react";
import { Link, useLocation } from "react-router-dom";
import { pointsHistoryHref, pointsHistoryLabel } from "@/shared/config/pointsHistory";
import { usePointsHistoryShown } from "@/shared/lib/hooks/usePointsHistoryShown";
import cls from "./OwnPointsLink.module.scss";

/**
 * Своё число баллов «Тренировок» — вход в «Историю баллов» (владелец 12.10: «из цифр баллов
 * пользователя — чтобы он мог нажать на цифру и перейти на эту страницу откуда бы ни было»).
 * Отдельной кнопки «История баллов» нигде, кроме «Прогресса и лиги» → «Баллы», нет: ссылка —
 * само число (подпись для чтения с экрана — «История баллов»).
 *
 * Только СВОИ баллы «Тренировок»: у чужого профиля страница показала бы историю смотрящего, у
 * других сфер — не про них. Кому страница закрыта (POINTS_HISTORY_FOR) — число как было.
 * Вглубь — push со state.from: «‹» страницы вернёт туда, откуда нажали.
 */
export type OwnPointsHistory = { to: string; state: { from: string }; label: string };

/** Куда ведёт своё число баллов «Тренировок»; страница закрыта этому человеку — null. */
export const useOwnPointsHistory = (lang: string, week?: string | null): OwnPointsHistory | null => {
    const { shown } = usePointsHistoryShown();
    const location = useLocation();
    if (!shown) return null;
    return {
        to: pointsHistoryHref(lang, week),
        state: { from: location.pathname + location.search },
        label: pointsHistoryLabel(lang),
    };
};

/**
 * Число-ссылка: выглядит как было (цвет, шрифт — от места), нажимается целиком. Нет входа —
 * то же число без ссылки (обёрткой fallbackAs с тем же className, по умолчанию — без обёртки).
 */
export const OwnPointsLink: React.FC<{
    lang: string;
    /** ISO-неделя «2026-W40» — сразу экран этой недели. */
    week?: string | null;
    className?: string;
    testId?: string;
    /** Нет входа: обернуть число в этот тег с тем же className (иначе — как есть). */
    fallbackAs?: "span" | "div";
    children: React.ReactNode;
}> = ({ lang, week, className, testId = "own-points-history", fallbackAs, children }) => {
    const go = useOwnPointsHistory(lang, week);
    if (!go) {
        if (!fallbackAs) return <>{children}</>;
        return React.createElement(fallbackAs, { className }, children);
    }
    return (
        <Link
            to={go.to}
            state={go.state}
            className={`${cls.link}${className ? ` ${className}` : ""}`}
            aria-label={go.label}
            title={go.label}
            data-testid={testId}
            data-points-history=""
        >
            {children}
        </Link>
    );
};

export default OwnPointsLink;
